/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// Lib dependencies
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

// Components
import { Button } from '@/components/UI/Button';
import Modal from '@/components/UI/Modal';
import { DatePicker } from '@/components/Form/DatePicker/DatePicker';
import { Combobox } from '@/components/Combobox/Combobox';

// Hooks
import { useBeneficiaries } from '../../Beneficiaries/hooks/beneficiaries.hook';
import { useAddMeeting } from '../hooks/add-meeting.hook';
import { Alert } from '@/components/UI/Alert';

// Types
/* -------------------------------------------------------------------------- */
/*                              Validation Schema                             */
/* -------------------------------------------------------------------------- */
const meetingSchema = z.object({
  beneficiary: z.string().min(1, 'validation.beneficiary_required'),
  meetingDate: z.date().refine((date) => date > new Date(), {
    message: 'validation.meeting_date_in_futur',
  }),
  startAt: z.date(),
});

type MeetingValues = z.infer<typeof meetingSchema>;

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const AddMeetingModal = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data, isFetching } = useBeneficiaries({
    params: { search: searchQuery, pageSize: 20 },
  });

  const beneficiaries =
    data?.pages
      .flatMap((item) => item.results)
      .map((beneficiary) => ({
        label: beneficiary.first_name + ' ' + beneficiary.last_name,
        value: beneficiary.id,
        status: beneficiary.status,
      })) ?? [];

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<MeetingValues>({
    resolver: zodResolver(meetingSchema),
    defaultValues: {
      meetingDate: new Date(),
      startAt: new Date(),
      beneficiary: '',
    },
  });

  const modal = useModal();

  const { mutate: addMeeting, isPending } = useAddMeeting({
    onSuccess: () => {
      modal.remove();
    },
  });

  const handleClose = () => {
    modal.remove();
  };

  const onSubmit = (data: MeetingValues) => {
    const year = data.meetingDate.getFullYear();
    const month = String(data.meetingDate.getMonth() + 1).padStart(2, '0');
    const day = String(data.meetingDate.getDate()).padStart(2, '0');

    const hour = String(data.meetingDate.getHours()).padStart(2, '0');
    const minute = String(data.meetingDate.getMinutes()).padStart(2, '0');

    const formattedMeetingDate = `${year}-${month}-${day}`;

    const formattedStartAt = `${hour}:${minute}`;

    addMeeting({
      beneficiaryId: data.beneficiary,
      meetingDate: formattedMeetingDate,
      startAt: formattedStartAt,
    });
  };

  return (
    <Modal
      visible={modal.visible}
      onCancel={handleClose}
      title={t('pages.meetings.create_a_meeting')}
    >
      <div>{t('pages.meetings.pick_beneficiary_and_date')}</div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Combobox
              label={t('pages.meetings.select_beneficiary')}
              placeholder={t('pages.meetings.select_beneficiary')}
              items={beneficiaries}
              selectedItem={beneficiaries.find(
                (item) => item.value === watch('beneficiary')
              )}
              onSelect={(item: { value: string }) => {
                setValue('beneficiary', item.value);
              }}
              triggerClassName="mt-2"
              onSeachChange={setSearchQuery}
              isLoading={isFetching}
              error={errors.beneficiary}
            />
          </div>

          <div className="grid gap-2">
            <DatePicker
              date={watch('meetingDate')}
              label={t('pages.meetings.meeting_date')}
              setDate={(selectedDate) => {
                if (selectedDate) {
                  const updatedStartAt = new Date(selectedDate);

                  setValue('meetingDate', selectedDate);
                  setValue('startAt', updatedStartAt);
                }
              }}
              fromDate={new Date()}
              showTime={true}
              maxDate={
                new Date(new Date().setFullYear(new Date().getFullYear() + 1))
              }
              className="bg-gray-200 mt-2"
              error={errors.meetingDate}
            />
          </div>
        </div>

        {beneficiaries.find(
          (item) =>
            item.value === watch('beneficiary') && item.status === 'INACTIVE'
        ) && (
          <Alert variant="destructive" className="my-4">
            {t('global.beneficiary_is_inactive')}
          </Alert>
        )}

        <Button type="submit" className="w-full" isLoading={isPending}>
          {t('pages.meetings.create_meeting')}
        </Button>
      </form>
    </Modal>
  );
};

export default NiceModal.create(AddMeetingModal);
